import { useState, useEffect } from 'react';
import { LibraryDB } from '../services/LibraryDB';
import { offlineStore } from '../lib/OfflineStore';
import { downloadManager } from '../lib/DownloadManager';
import { getCoverUrl } from '../utils/coverHelper';

interface Props {
    onOpenBook: (id: string) => void;
    manifest: any[]; // Used to determine if Book Club
    onDiscover?: () => void;
    [key: string]: any; // Loose for now to avoid other breakages or strictness
}

const checkIsPwaInstalled = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.matchMedia('(display-mode: fullscreen)').matches ||
           window.matchMedia('(display-mode: minimal-ui)').matches ||
           (navigator as any).standalone === true;
};

export const LibraryView = (props: Props) => {
    const { onOpenBook, manifest } = props;
    const [books, setBooks] = useState<any[]>([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Offline Status Maps
    const [downloadedAudio, setDownloadedAudio] = useState<Record<string, boolean>>({});
    const [downloadedText, setDownloadedText] = useState<Record<string, boolean>>({});
    const [localCovers, setLocalCovers] = useState<Record<string, string>>({});
    const [isPwa, setIsPwa] = useState(false);

    useEffect(() => {
        setIsPwa(checkIsPwaInstalled());
    }, []);

    useEffect(() => {
        loadLibrary();
    }, [refreshTrigger, manifest]);

    useEffect(() => {
        let active = true;
        const newLocalCovers: Record<string, string> = {};
        const blobUrlsToRevoke: string[] = [];

        const loadCovers = async () => {
            for (const book of books) {
                if (book.audioDownloadState === 'downloaded_verified') {
                    try {
                        const blob = await offlineStore.getChunk(book.id, 'cover.webp');
                        if (blob && blob.size > 0) {
                            const url = URL.createObjectURL(blob);
                            newLocalCovers[book.id] = url;
                            blobUrlsToRevoke.push(url);
                        }
                    } catch (e) {
                        console.warn(`Failed to load local cover for ${book.id}`, e);
                    }
                }
            }
            if (active) {
                setLocalCovers(newLocalCovers);
            }
        };

        loadCovers();

        return () => {
            active = false;
            blobUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
        };
    }, [books]);

    useEffect(() => {
        const handler = (status: any) => {
            setBooks(prevBooks => {
                return prevBooks.map(b => {
                    if (b.id === status.bookId) {
                        let audioDownloadState: any = 'not_downloaded';
                        if (status.state === 'downloading') audioDownloadState = 'downloading';
                        else if (status.state === 'paused') audioDownloadState = 'partial';
                        else if (status.state === 'completed') audioDownloadState = 'downloaded_verified';
                        else if (status.state === 'error') audioDownloadState = 'failed';

                        return {
                            ...b,
                            audioDownloadState,
                            audioDownloadProgress: status.progress
                        };
                    }
                    return b;
                });
            });
        };

        downloadManager.addListener(handler);
        return () => {
            downloadManager.removeListener(handler);
        };
    }, []);

    const loadLibrary = async () => {
        try {
            const allBooks = await LibraryDB.getAllBooks();
            allBooks.sort((a, b) => b.addedAt - a.addedAt);
            setBooks(allBooks);
            checkOfflineStatus(allBooks);
        } catch (e) {
            console.error("Failed to load library", e);
        }
    };

    const checkOfflineStatus = async (bookList: any[]) => {
        try {
            const stats = await offlineStore.getAllBookStats();
            const audioMap: Record<string, boolean> = {};
            stats.forEach(s => { if (s.totalBytes > 0) audioMap[s.bookId] = true; });
            setDownloadedAudio(audioMap);
        } catch (e) { console.warn(e); }

        const textMap: Record<string, boolean> = {};
        await Promise.all(bookList.map(async (b) => {
            const asset = await LibraryDB.getBookAsset(b.id);
            if (asset?.text) textMap[b.id] = true;
        }));
        setDownloadedText(textMap);
    };

    // Actions
    const handleDelete = async (id: string, e: any) => {
        e.stopPropagation();
        if (confirm("Remove this book from library? (Files will be kept if downloaded)")) {
            await LibraryDB.deleteBook(id);
            setRefreshTrigger(p => p + 1);
        }
    };

    const handleDownloadAudio = (book: any, e: any) => {
        e.stopPropagation();
        if (!isPwa) {
            alert("Install the app to enable reliable airplane-mode downloads.");
            return;
        }
        if (book.isBookClub) {
            downloadManager.startDownload(book.id, `/api/library/playlist/${book.id}`);
        }
    };

    // Image Fallback Handler
    const handleImageError = (e: any, book: any) => {
        const img = e.target as HTMLImageElement;
        const currentSrc = img.src;

        // Fallback chain: cover_grid -> cover_full -> cover_hi -> cover_master.png -> placeholder
        if (currentSrc.includes('cover_grid.webp')) {
            img.src = getCoverUrl(book.coverUrl, 'full', book.id);
        } else if (currentSrc.includes('cover_full.webp')) {
            img.src = getCoverUrl(book.coverUrl, 'hi', book.id);
        } else if (currentSrc.includes('cover_hi.webp')) {
            img.src = getCoverUrl(book.coverUrl, 'master', book.id);
        } else {
            // Give up
            img.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
        }
    };

    if (books.length === 0) {
        return (
            <div style={{ textAlign: 'center', marginTop: '50px', color: 'var(--text-secondary)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '20px' }}>📚</div>
                <h3>Your Library is Empty</h3>
                <p>Go to "Discover" to find and save books.</p>
                <button
                    onClick={() => {
                        if (props.onDiscover) props.onDiscover();
                    }}
                    style={{
                        marginTop: '20px', padding: '10px 20px', fontSize: '1rem',
                        background: 'var(--accent-color)', color: 'white',
                        border: 'none', borderRadius: '6px', cursor: 'pointer'
                    }}
                >
                    Go to Discover
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', height: 'calc(100vh - 50px)', overflowY: 'auto' }}>
            <h2 style={{ marginBottom: '20px' }}>My Library</h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))', gap: '20px', paddingBottom: '40px' }}>
                {books.map(rawBook => {
                    // Normalize inputs
                    const book = {
                        ...rawBook,
                        id: rawBook.id || 'unknown',
                        title: rawBook.title || 'Untitled',
                        isBookClub: !!rawBook.isBookClub,
                        coverUrl: rawBook.coverUrl || '',
                    };

                    const safeManifest = Array.isArray(manifest) ? manifest : [];
                    const isBookClub = book.isBookClub || safeManifest.some((m: any) => m && m.id === book.id);
                    const hasAudio = !!downloadedAudio[book.id];
                    const hasText = !!downloadedText[book.id];
                    // Cache Busting
                    const mItem = Array.isArray(manifest) ? manifest.find(m => m.id === book.id) : null;
                    const coverVersion = mItem?.ts || book.addedAt || 0;

                    const gridCover = getCoverUrl(book.coverUrl, 'grid', book.id);
                    const coverSrc = localCovers[book.id] || (gridCover ? `${gridCover}?v=${coverVersion}` : '');

                    return (
                        <div key={book.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%' }}>
                            <div
                                onClick={() => {
                                    if (typeof onOpenBook === 'function') {
                                        onOpenBook(book.id);
                                    } else {
                                        console.error('[LibraryView] onOpenBook prop missing or invalid', {
                                            bookId: book.id,
                                            title: book.title,
                                            propType: typeof onOpenBook
                                        });
                                    }
                                }}
                                style={{
                                    aspectRatio: '2/3', background: '#333', borderRadius: '8px',
                                    overflow: 'hidden', position: 'relative', cursor: 'pointer',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                }}
                            >
                                {coverSrc ? (
                                    <img
                                        src={coverSrc}
                                        alt={book.title}
                                        loading="lazy"
                                        decoding="async"
                                        width="140"
                                        height="210"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => handleImageError(e, book)}
                                    />
                                ) : (
                                    <div style={{ padding: '10px', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        No Cover
                                    </div>
                                )}

                                {/* Badges */}
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '5px', background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    {hasText && (
                                        <span style={{ fontSize: '0.7em', background: '#4CAF50', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>
                                            TEXT ✓
                                        </span>
                                    )}

                                    {book.audioDownloadState === 'downloaded_verified' ? (
                                        <span 
                                            style={{ fontSize: '0.75em', background: '#4CAF50', color: 'white', padding: '2px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '2px' }}
                                            title="Downloaded for Airplane Mode"
                                        >
                                            ✈️
                                        </span>
                                    ) : book.audioDownloadState === 'downloading' ? (
                                        <span 
                                            style={{ fontSize: '0.7em', background: '#FF9800', color: 'white', padding: '2px 6px', borderRadius: '4px' }}
                                        >
                                            Downloading {book.audioDownloadProgress || 0}%
                                        </span>
                                    ) : book.audioDownloadState === 'partial' ? (
                                        <span 
                                            style={{ fontSize: '0.75em', background: '#FF9800', color: 'white', padding: '2px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '2px' }}
                                            title="Partial download"
                                        >
                                            ⚠️ Partial
                                        </span>
                                    ) : book.audioDownloadState === 'failed' ? (
                                        <span 
                                            style={{ fontSize: '0.75em', background: '#F44336', color: 'white', padding: '2px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '2px' }}
                                            title="Download failed"
                                        >
                                            ⚠️ Failed
                                        </span>
                                    ) : (isBookClub || hasAudio) && (
                                        <span style={{
                                            fontSize: '0.7em',
                                            background: hasAudio ? '#2196F3' : 'rgba(0,0,0,0.6)',
                                            border: hasAudio ? 'none' : '1px solid #aaa',
                                            color: 'white', padding: '2px 6px', borderRadius: '4px',
                                            display: 'flex', alignItems: 'center', gap: '2px'
                                        }}>
                                            AUDIO {hasAudio ? '✓' : '⬇️'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div style={{ fontSize: '0.95em', fontWeight: 600, lineHeight: 1.2, height: '2.4em', overflow: 'hidden' }}>
                                {book.title}
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: '5px', marginTop: 'auto' }}>
                                {isBookClub && book.audioDownloadState !== 'downloaded_verified' && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isPwa) {
                                                alert("Install the app to enable reliable airplane-mode downloads.");
                                                return;
                                            }
                                            if (book.audioDownloadState === 'downloading') {
                                                downloadManager.pauseDownload(book.id);
                                            } else {
                                                downloadManager.startDownload(book.id, `/api/library/playlist/${book.id}`);
                                            }
                                        }}
                                        style={{
                                            flex: 1, padding: '6px', fontSize: '0.8em',
                                            background: 'var(--surface-color)', border: '1px solid var(--border-color)',
                                            borderRadius: '4px', cursor: isPwa ? 'pointer' : 'not-allowed', color: isPwa ? 'var(--text-color)' : 'var(--text-disabled)',
                                            opacity: isPwa ? 1 : 0.6
                                        }}
                                        title={
                                            isPwa 
                                                ? (book.audioDownloadState === 'downloading' 
                                                    ? "Pause Download" 
                                                    : (book.audioDownloadState === 'partial' || book.audioDownloadState === 'failed' ? "Repair Download" : "Download Audio")) 
                                                : "Install the app to enable reliable airplane-mode downloads."
                                        }
                                    >
                                        {!isPwa 
                                            ? "⬇️ Audio" 
                                            : (book.audioDownloadState === 'downloading' 
                                                ? "⏸ Pause" 
                                                : (book.audioDownloadState === 'partial' || book.audioDownloadState === 'failed' 
                                                    ? "🔄 Repair" 
                                                    : "⬇️ Audio"))
                                        }
                                    </button>
                                )}

                                <button
                                    onClick={(e) => handleDelete(book.id, e)}
                                    style={{
                                        padding: '6px 10px', fontSize: '0.8em',
                                        background: 'var(--surface-color)', border: '1px solid var(--border-color)',
                                        borderRadius: '4px', cursor: 'pointer', color: 'var(--text-secondary)'
                                    }}
                                    title="Remove"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
